import { isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { useOriginalDocumentsColumnDefs } from '../../../hooks/useOriginalDocumentsColumnDefs';
import { AgreementType, fetchAllAgreementTypeRelationshipsStarted, fetchAllAgreementTypesStarted, fetchAllDocumentGroupsStarted, fetchAvailableDocumentNamesStarted, fetchMLReadyAgreementTypeIdsStarted, getAgreementTypeRelationships, getAllAgreementTypes, getAllClientDocumentGroups, getAvailableDocumentNames } from '../../admin/documents/store';
import { fetchAllEntitiesStarted, getBaseEntityDropdownOptions } from '../../admin/entity/store';
import { BasicUserInfo, ClientFeaturePermission, FeaturePermission, fetchAllBasicUsersStarted, getAllBasicUsers } from '../../admin/users/store';
import { getClientHasFeaturePermission, getUserHasFeaturePermission, getUserId, getInternetGatewayOpen } from '../../auth/login/store';
import { Analytics, Delete, Document, Download, Export, Link, Pencil, Robot, SaveFile } from '../../shared/icons';
import { Action } from '../../shared/modal/ActionModal';
import { ArkTable } from '../../shared/table/ArkTable';
import { ColumnSort, TableFilterType } from '../../shared/table/types';
import { DeleteDocumentConfirmationModal } from './DeleteDocumentConfirmationModal';
import { DocumentLinkModal } from './DocumentLinkModal';
import { OriginalDocumentAgreementLinkModal } from './OriginalDocumentAgreementLinkModal';
import { RenameDocumentModal } from './RenameDocumentModal';
import { clearOriginalDocumentTableFilters, downloadAllDocumentsStarted, downloadDocumentStarted, exportToCDMStarted, exportToCSVStarted, fetchDocumentsStarted, getDeleteDocumentModalOpen, getDocumentCustomFilterHasUpdated, getIsLoading, getLinkedDocumentsModalOpen, getOriginalDocumentColumnSort, getOriginalDocumentFilters, getOriginalDocumentPageSize, getOriginalDocumentsPageNumber, getOriginalTableDocuments, getRenameDocumentModalOpen, getSelectedCustomFilterName, getSelectedDocumentCustomFilterId, getTotalOriginalDocuments, linkDocumentStarted, openDocumentStarted, originalDocumentsPaginationNext, originalDocumentsPaginationPrevious, reanalyzeDocumentStarted, runLatestMLQueriesStarted, setOriginalDocumentTableColumnSort, setOriginalDocumentTableFilters, setOriginalDocumentsPageSize, toggleDeleteConfirmationModal, toggleLinkDocumentsModal, toggleRenameDocumentModal, updateDocumentCustomFilters } from './store';
import { DropdownOption } from '../../shared/dropdown/Dropdown';
import { DisabledDocumentModal } from './DisabledDocumentModal';
import { ArkTableDocument } from '../store/types';
import { isCDMDocumentName } from '../../admin/documents/store/typeAssertions';

export const OriginalDocumentsTable: React.FC = () => {
    const [disabledDocumentModalOpen, setDisabledDocumentModalOpen] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const pageNumber = useAppSelector(getOriginalDocumentsPageNumber);
    useFetchStarted([fetchDocumentsStarted(pageNumber), fetchDocumentsStarted(pageNumber, AgreementType.ISDA), fetchAvailableDocumentNamesStarted(), fetchAllBasicUsersStarted(), fetchAllEntitiesStarted(true), fetchAllDocumentGroupsStarted(), fetchMLReadyAgreementTypeIdsStarted(), fetchAllAgreementTypeRelationshipsStarted(), fetchAllAgreementTypesStarted()]);
    const tableDocuments = useAppSelector(getOriginalTableDocuments);
    const isLoading = useAppSelector(getIsLoading);
    const allDocumentNames = useAppSelector(getAvailableDocumentNames);
    const allClientDocumentGroups = useAppSelector(getAllClientDocumentGroups);
    const entityOptions = useAppSelector(getBaseEntityDropdownOptions);
    const allUsers = useAppSelector(getAllBasicUsers);
    const userId = useAppSelector(getUserId);

    const hasRAFExportPermission = useAppSelector(getClientHasFeaturePermission([ClientFeaturePermission.EXPORT_RAF_DOCUMENTS_TO_CSV]));
    const hasDocumentExtractAIPermission = useAppSelector(getClientHasFeaturePermission([ClientFeaturePermission.DOCUMENT_EXTRACT_WITH_AI]));
    const hasOpinionModule = useAppSelector(getClientHasFeaturePermission([ClientFeaturePermission.OPINION_MODULE]));
    const linkedDocumentsModalOpen = useAppSelector(getLinkedDocumentsModalOpen);
    const hasDownloadPermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.DOWNLOAD_DOCUMENTS]));
    const hasDeletePermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.DELETE_DOCUMENTS]));
    const hasCompleteDocumentPermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.COMPLETE_DOCUMENT_DETAILS]));

    const deleteDocumentModalOpen = useAppSelector(getDeleteDocumentModalOpen);
    const renameDocumentModalOpen = useAppSelector(getRenameDocumentModalOpen);

    const totalDocuments = useAppSelector(getTotalOriginalDocuments);
    const documentFilters = useAppSelector(getOriginalDocumentFilters);
    const columnSort = useAppSelector(getOriginalDocumentColumnSort);
    const pageSize = useAppSelector(getOriginalDocumentPageSize);
    const documentFilterUpdated = useAppSelector(getDocumentCustomFilterHasUpdated);
    const selectedFilterId = useAppSelector(getSelectedDocumentCustomFilterId);
    const selectedFilterName = useAppSelector(getSelectedCustomFilterName);
    const agreementTypeRelationships = useAppSelector(getAgreementTypeRelationships);
    const allAgreementTypes = useAppSelector(getAllAgreementTypes);
    const internetGatewayOpen = useAppSelector(getInternetGatewayOpen);

    const getUserLabel = useCallback((user: BasicUserInfo) => {
        if (user.userId === userId) {
            return 'Me';
        }
        const name = user.forenames.length > 0 && user.surname.length > 0 ? `${user.forenames} ${user.surname} (${user.username})` : user.username;
        return name;
    }, [userId]);
    const basicUserOptions: DropdownOption[] = useMemo(() => allUsers.map(user => ({ value: user.userId.toString(), label: getUserLabel(user), showDivider: user.userId === userId })).sort((a, b) => {
        if (a.value === userId?.toString() || b.value === userId?.toString()) {
            return a.value === userId?.toString() ? -1 : 1;
        }
        return a.label.localeCompare(b.label);
    }), [allUsers, getUserLabel, userId]);

    const nextPage = useCallback(() => dispatch(originalDocumentsPaginationNext()), [dispatch]);
    const previousPage = useCallback(() => dispatch(originalDocumentsPaginationPrevious()), [dispatch]);
    const updateFilter = useCallback((key: string, value: string | string[] | null, type: keyof TableFilterType = 'text') => dispatch(setOriginalDocumentTableFilters(key, value, type)), [dispatch]);
    const clearAllFilters = useCallback(() => dispatch(clearOriginalDocumentTableFilters()), [dispatch]);
    const updatePageSize = useCallback((pageSize: number) => dispatch(setOriginalDocumentsPageSize(pageSize)), [dispatch]);

    const openDocument = useCallback(({ documentId, location, mimeType }: ArkTableDocument) => dispatch(openDocumentStarted(location, documentId, mimeType)), [dispatch]);

    const deleteDocument = useCallback((document: ArkTableDocument) => dispatch(toggleDeleteConfirmationModal(document)), [dispatch]);

    const saveDocument = useCallback(({ location, mimeType, documentDescription }: ArkTableDocument) => dispatch(downloadDocumentStarted(location, mimeType, documentDescription)), [dispatch]);

    const saveAllDocuments = useCallback(({ allDocuments, documentDescription }: ArkTableDocument) => dispatch(downloadAllDocumentsStarted(allDocuments.map(({ location }) => location), documentDescription)), [dispatch]);

    const openLinkDocumentsModal = useCallback(({ documentId, entityA, entityB }: ArkTableDocument, agreementTypeId: number) => {
        const entityAName = entityA.entityName ? entityA.entityName : undefined;
        const entityBName = entityB.entityName ? entityB.entityName : undefined;
        dispatch(fetchDocumentsStarted(1, agreementTypeId, undefined, entityAName, entityBName));
        dispatch(toggleLinkDocumentsModal(documentId));
    }, [dispatch]);

    const exportToCSV = useCallback(({ documentNameId, documentId, documentDescription }: ArkTableDocument) => dispatch(exportToCSVStarted(documentNameId!, documentId, documentDescription)), [dispatch]);

    const exportToCDM = useCallback((documentId: number) => dispatch(exportToCDMStarted(documentId, true)), [dispatch]);

    const reanalyzeDocument = useCallback((location: string) => dispatch(reanalyzeDocumentStarted(location)), [dispatch]);

    const openDocumentRenameModal = useCallback((document: ArkTableDocument) => dispatch(toggleRenameDocumentModal(document)), [dispatch]);

    const runLatestMLQueries = useCallback((documentId: number) => dispatch(runLatestMLQueriesStarted(documentId)), [dispatch]);

    const getLatestMLAvailable = useCallback((document: ArkTableDocument) => !isUndefined(document.latestMlDataVersion) && !isUndefined(document.mlDataVersion) && !document.mlInProgress && hasDocumentExtractAIPermission && document.latestMlDataVersion > document.mlDataVersion, [hasDocumentExtractAIPermission]);

    const getAgreementTypeLinkOptions = useCallback((agreementTypeId: number | null) => agreementTypeRelationships.reduce((acc: number[], cur) => {
        if (isNull(agreementTypeId)) {
            return acc;
        }
        return cur.childAgreementTypeId === agreementTypeId ? [...acc, cur.parentAgreementTypeId] : acc;
    }, []), [agreementTypeRelationships]);

    const [agreementLinkModalDocument, setAgreementLinkModalDocument] = useState<ArkTableDocument | null>(null);
    const [parentOptions, setParentOptions] = useState<DropdownOption[]>([]);
    const [parentOption, setParentOption] = useState<DropdownOption | null>(null);

    const openAgreementTypeLinkModal = useCallback((dropdownOptions: DropdownOption[], data: ArkTableDocument) => {
        setParentOptions(dropdownOptions);
        setAgreementLinkModalDocument(data);
    }, []);

    const closeAgreementTypeLinkModal = useCallback(() => {
        setAgreementLinkModalDocument(null);
        setParentOption(null);
    }, []);

    const setParentAgreementType = useCallback((parentOption: DropdownOption | Options<DropdownOption> | null) => setParentOption(parentOption as DropdownOption), []);

    const confirmParentLink = useCallback(() => {
        if (!isNull(parentOption)) {
            closeAgreementTypeLinkModal();
            openLinkDocumentsModal(agreementLinkModalDocument!, parseInt((parentOption as DropdownOption).value));
        }
    }, [openLinkDocumentsModal, closeAgreementTypeLinkModal, agreementLinkModalDocument, parentOption]);

    const documentActions = useCallback((data: ArkTableDocument): Action[] => {
        const { agreementTypeId, linkedDocumentId, allDocuments, analysisComplete, analysisStarted, location, documentId, documentNameId, documentDisabled } = data;
        const showLatestMLAvailable = getLatestMLAvailable(data);
        const documentAgreementLinks = getAgreementTypeLinkOptions(agreementTypeId);
        const canLinkToDocuments = !isNull(agreementTypeId) && documentAgreementLinks.length > 0 && !linkedDocumentId;
        const hasMultipleAgreementLinks = documentAgreementLinks.length > 1;

        let dropdownOptions: DropdownOption[] = [];
        if (canLinkToDocuments) {
            const parentTypes = allAgreementTypes.filter(({ agreementTypeId }) => documentAgreementLinks.includes(agreementTypeId));
            dropdownOptions = parentTypes.map(({ agreementTypeId, name }) => ({ label: name, value: agreementTypeId.toString() }));
        }

        if (documentDisabled === 1) {
            let options: Action[] = [{ label: 'Open', icon: Document, onClick: () => setDisabledDocumentModalOpen(true), withSeparator: hasDeletePermission }];
            if (hasDeletePermission) {
                options.push({ label: 'Delete', icon: Delete, onClick: () => deleteDocument(data), isDelete: true });
            }
            return options;
        }

        let options: Action[] = [
            { label: 'Open', icon: Document, onClick: () => openDocument(data) }
        ];
        if (hasDownloadPermission && !internetGatewayOpen) {
            options.push(
                { label: 'Download', icon: SaveFile, onClick: () => saveDocument(data) },
                { label: `Download All (${allDocuments.length})`, icon: Download, onClick: () => saveAllDocuments(data) }
            );
        }
        if (!analysisComplete && analysisStarted === 0) {
            options.splice(1, 0, { label: 'Analyze', icon: Analytics, onClick: () => reanalyzeDocument(location) });
        }
        if (canLinkToDocuments) {
            options.splice(3, 0, { label: 'Link to "parent" Document', icon: Link, onClick: () => hasMultipleAgreementLinks ? openAgreementTypeLinkModal(dropdownOptions, data) : openLinkDocumentsModal(data, documentAgreementLinks[0]) });
        }
        if (agreementTypeId === AgreementType.RAF_REVIEW && hasRAFExportPermission) {
            options.splice(3, 0, { label: 'Export to CSV', icon: Export, onClick: () => exportToCSV(data) });
        }
        if (documentNameId && isCDMDocumentName(documentNameId)) {
            options.splice(3, 0, { label: 'Export to CDM model', icon: Export, onClick: () => exportToCDM(documentId) });
        }
        if (showLatestMLAvailable) {
            // TODO: This should run the latest ML queries for either the original or the latest restatement in the timeline.
            options.push({ label: 'Run Latest AI', icon: Robot, onClick: () => runLatestMLQueries(documentId) });
        }
        if (hasCompleteDocumentPermission) {
            options.push({ label: 'Rename', icon: Pencil, onClick: () => openDocumentRenameModal(data), withSeparator: hasDeletePermission });
        }
        if (hasDeletePermission) {
            options.push({ label: 'Delete', icon: Delete, onClick: () => deleteDocument(data), isDelete: true });
        }
        return options;
    }, [deleteDocument, openDocument, openLinkDocumentsModal, reanalyzeDocument, saveAllDocuments, saveDocument, exportToCSV, openDocumentRenameModal, hasRAFExportPermission, hasCompleteDocumentPermission, hasDeletePermission, hasDownloadPermission, getLatestMLAvailable, runLatestMLQueries, exportToCDM, getAgreementTypeLinkOptions, openAgreementTypeLinkModal, allAgreementTypes, internetGatewayOpen]);

    const columnDefs = useOriginalDocumentsColumnDefs(documentActions, hasOpinionModule);

    const selectedDocumentId = useMemo(() => {
        if (isNull(linkedDocumentsModalOpen)) {
            return null;
        }
        return tableDocuments.find(({ documentId }) => documentId === linkedDocumentsModalOpen)?.linkedDocumentId || null;
    }, [linkedDocumentsModalOpen, tableDocuments]);

    const closeLinkDocumentsModal = useCallback(() => { dispatch(toggleLinkDocumentsModal(null)); }, [dispatch]);

    const linkDocumentToOriginal = useCallback((documentId: number) => {
        dispatch(linkDocumentStarted(linkedDocumentsModalOpen!, documentId));
        closeLinkDocumentsModal();
    }, [dispatch, closeLinkDocumentsModal, linkedDocumentsModalOpen]);

    const toggleColumnSort = useCallback((columnSort: ColumnSort) => { dispatch(setOriginalDocumentTableColumnSort(columnSort)); }, [dispatch]);

    const documentNameOptions = useMemo(() => allDocumentNames
        .filter(({ datasetId }) => datasetId)
        .map(({ documentName }) => ({ value: documentName, label: documentName })), [allDocumentNames]
    );
    const clientDocumentGroupOptions = useMemo(() => allClientDocumentGroups.map(({ groupName, clientDocumentGroupId }, i) => ({ value: clientDocumentGroupId!.toString(), label: groupName, showDivider: i === allClientDocumentGroups.length - 1 })), [allClientDocumentGroups]);

    const documentsAndDocumentGroupOptions = useMemo(() => [...clientDocumentGroupOptions, ...documentNameOptions], [documentNameOptions, clientDocumentGroupOptions]);

    const filterDropdownOptions = useMemo(() => ({
        documentName: documentsAndDocumentGroupOptions,
        entityA: entityOptions,
        entityB: entityOptions,
        createdBy: basicUserOptions,
        modifiedBy: basicUserOptions
    }), [documentsAndDocumentGroupOptions, entityOptions, basicUserOptions]);

    const saveCustomFilters = useCallback((label: string, createNew: boolean) => dispatch(updateDocumentCustomFilters(label, createNew)), [dispatch]);

    const doubleRowClick = useCallback((row: ArkTableDocument) => row.documentDisabled ? setDisabledDocumentModalOpen(true) : openDocument(row), [openDocument]);

    return (
        <>
            <ArkTable
                colDefs={columnDefs}
                rowData={tableDocuments}
                testId='original-documents'
                isLoading={isLoading}
                page={pageNumber}
                total={totalDocuments}
                next={nextPage}
                previous={previousPage}
                filters={documentFilters}
                updateFilter={updateFilter}
                clearAllFilters={clearAllFilters}
                onRowDoubleClicked={row => doubleRowClick(row as ArkTableDocument)}
                columnSort={columnSort}
                toggleSort={toggleColumnSort}
                pageSize={pageSize}
                updatePageSize={updatePageSize}
                filterDropdownOptions={filterDropdownOptions}
                saveCustomFilters={saveCustomFilters}
                saveButtonIsVisible={documentFilterUpdated}
                customFilterName={selectedFilterName}
                isExistingCustomFilter={!isNull(selectedFilterId)}
                removeTextFilter={['createdBy', 'modifiedBy']}
            />
            {!isNull(linkedDocumentsModalOpen) &&
                <DocumentLinkModal
                    select={linkDocumentToOriginal}
                    close={closeLinkDocumentsModal}
                    selectedId={selectedDocumentId}
                />
            }
            <DeleteDocumentConfirmationModal isOpen={deleteDocumentModalOpen} />
            <RenameDocumentModal isOpen={renameDocumentModalOpen} isPermitted={hasCompleteDocumentPermission} />
            <OriginalDocumentAgreementLinkModal
                isOpen={!isNull(agreementLinkModalDocument)}
                closeModal={closeAgreementTypeLinkModal}
                dropdownOption={parentOption}
                dropdownOptions={parentOptions}
                setParentAgreementType={setParentAgreementType}
                confirmParentLink={confirmParentLink}
            />
            <DisabledDocumentModal isOpen={disabledDocumentModalOpen} closeModal={() => setDisabledDocumentModalOpen(false)} />
        </>
    );
};
